import RigIcon from '~/images/icons/rig-icon-circle.svg';
import cx from 'classnames';
import { observer, useLocalObservable } from 'mobx-react';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect } from 'react';

const SearchAutocomplete = observer(
  ({
    removeAllPopups,
    search,
    searchTerm,
    selectedCountryCode,
    showUserImpact,
    userId,
  }) => {
    const localStore = useLocalObservable(() => ({
      loading: false,
      results: [],
      resultsCount: 0,
      selectedIndex: 0,
      searchTerm: searchTerm,
      selectedValue: null,
      showInput: false,
      toggleInput: function () {
        this.showInput = !this.showInput;
        removeAllPopups();
      },
      get showResults() {
        return localStore.results.length > 0 && localStore.showInput;
      },
    }));

    useEffect(() => {
      localStore.selectedValue = searchTerm;
      localStore.searchTerm = searchTerm;
      localStore.showInput = false;
    }, [searchTerm]);

    const [fetchAutocompleteResults] = useDebouncedCallback(() => {
      $.getJSON(autocompleteUrl(), (response) => {
        localStore.loading = false;

        if (response) {
          localStore.results = response.projects;
          localStore.resultsCount = response.count;
        } else {
          localStore.results = [];
          localStore.resultsCount = 0;
          resetSearch();
        }
      });
    }, 500);

    const autocompleteUrl = () => {
      const path = '/api/v1/projects/autocomplete';
      const params = showUserImpact
        ? `search=${localStore.searchTerm}&user_id=${userId}&country=${selectedCountryCode}`
        : `search=${localStore.searchTerm}&country=${selectedCountryCode}`;
      return `${path}?${params}`;
    };

    const onSubmit = (event) => {
      event.preventDefault();
      localStore.results = [];
      localStore.selectedIndex = 0;
      search(localStore.searchTerm);
    };

    const onKeyDown = (event) => {
      if (
        event.key === 'ArrowDown' &&
        localStore.selectedIndex < localStore.results.length
      ) {
        localStore.selectedIndex += 1;
      }

      if (event.key === 'ArrowUp' && localStore.selectedIndex > 0) {
        localStore.selectedIndex -= 1;
      }

      if (event.key === 'Enter') {
        if (localStore.selectedIndex > 0) {
          localStore.searchTerm =
            localStore.results[localStore.selectedIndex - 1].deployment_code;
        }
      }
    };

    const resetSearch = () => {
      localStore.loading = false;
      localStore.selectedIndex = 0;
      localStore.selectedValue = null;
      localStore.searchTerm = '';
      search('');
    };

    const onChange = (event) => {
      localStore.loading = true;
      localStore.selectedIndex = 0;
      localStore.searchTerm = event.target.value.replace('\\', '');
      fetchAutocompleteResults();
    };

    const handleClick = (value) => {
      localStore.selectedValue = value;
      search(value);
      localStore.results = [];
    };

    const placeholderText = () => 'Enter your search term';

    const highlightedText = (text) => (
      <span
        dangerouslySetInnerHTML={{
          __html: text.replace(
            new RegExp(localStore.searchTerm, 'gi'),
            (match) => `<strong>${match}</strong>`
          ),
        }}
      />
    );

    const isUrlSearchParam = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const currentSearch = urlParams.get('search');
      return (
        currentSearch != '' &&
        currentSearch !== null &&
        currentSearch !== undefined
      );
    };

    return (
      <div className='project-autocomplete'>
        <div className='search-btn cursor-pointer rounded-full bg-cw-white body-s flex flex-row items-center justify-center text-cw-black'>
          <span className='preinput' />
          <span className='ml-1' onClick={localStore.toggleInput}>
            {window.isMobile ? '' : 'Search'}
          </span>
          {localStore.selectedValue && localStore.searchTerm && (
            <div className='result-bubble flex items-center justify-center absolute body-xs right-0 rounded-full bg-cw-brand-yellow-500'>
              1
            </div>
          )}
        </div>
        <div className='search-input-and-results absolute z-10 -mt-px'>
          {localStore.showInput && (
            <>
              <div className=' z-10 -mt-px border border-solid rounded border-grey-30 bg-cw-white project-autocomplete-results'>
                <span className='search-input-tip' />
                <span className='search-input-inner-tip' />

                <form onSubmit={onSubmit}>
                  {localStore.loading && (
                    <i className='absolute align-middle z-10 cw-icon-loop cw-icon-spin text-grey-40' />
                  )}
                  <input
                    autoComplete='off'
                    autoFocus={true}
                    className='p-2 text-cw-black'
                    name='search'
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    placeholder={placeholderText()}
                    type='text'
                    value={localStore.searchTerm}
                  />
                </form>
                {localStore.showResults && (
                  <>
                    {localStore.results.map((result, index) => (
                      <div
                        className={cx(
                          'px-1 md-px-2 py-1 project-autocomplete-result flex items-center',
                          { selected: localStore.selectedIndex == index + 1 }
                        )}
                        key={result.deployment_code}
                        onClick={() => handleClick(result.deployment_code)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                      >
                        {result.rig && (
                          <img
                            className='w-8 mr-1 rig md-w-10 text-grey-30'
                            src={RigIcon}
                          />
                        )}
                        <div>
                          <div className='p40'>
                            {highlightedText(result.community_name)}
                            &nbsp;&#183;&nbsp;
                            <span className='text-grey-60'>
                              {highlightedText(result.country)}
                            </span>
                          </div>
                          <div className='h80'>
                            {result.recognition_text && (
                              <>
                                {highlightedText(result.recognition_text)}
                                &nbsp;&#183;&nbsp;
                              </>
                            )}
                            <span className='text-grey-60'>
                              {result.rig
                                ? `${result.days_in_the_field} days in the field`
                                : result.year_completed}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div
                      className='flex items-center p-2 mt-1 border-0 border-t border-solid cursor-pointer border-grey-30'
                      onClick={() => handleClick(localStore.searchTerm)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <div className='md-w-3/4 p40'>
                        View all <strong>{localStore.resultsCount}</strong>{' '}
                        projects matching{' '}
                        <strong>&quot;{localStore.searchTerm}&quot;</strong>
                      </div>
                      {localStore.selectedIndex == 0 && (
                        <div className='w-1/3 text-right text-grey-40 h80 text-strong-heading show-for-medium-up'>
                          Filter Map &#8629;
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {isUrlSearchParam() && (
            <p
              className='clear-results text-right underline text-cw-white cursor-pointer'
              onClick={resetSearch}
            >
              clear search results
            </p>
          )}
        </div>
      </div>
    );
  }
);

export default SearchAutocomplete;
